import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { isAdminAuthed } from '@/lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'nextxus_library_card';

const LibraryCardContext = createContext(null);

export function useLibraryCard() {
  return useContext(LibraryCardContext);
}

export function LibraryCardProvider({ children }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlockedDocs, setUnlockedDocs] = useState([]);

  const isAdmin = isAdminAuthed() || card?.is_infinite || card?.card_type === 'admin';
  const tokenBalance = card?.is_infinite ? Infinity : (card?.token_balance || 0);
  const hasCard = !!card;

  // Load card from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.email) {
          axios.get(`${API}/library-card/verify`, { params: { email: parsed.email } })
            .then(res => {
              if (res.data.exists && res.data.card) {
                setCard(res.data.card);
                setUnlockedDocs(res.data.card.unlocked_documents || []);
              } else {
                localStorage.removeItem(STORAGE_KEY);
              }
            })
            .catch(() => localStorage.removeItem(STORAGE_KEY))
            .finally(() => setLoading(false));
          return;
        }
      } catch (e) {
        console.warn('Failed to parse stored library card:', e);
      }
    }
    setLoading(false);
  }, []);

  const register = useCallback(async (email, displayName) => {
    const res = await axios.post(`${API}/library-card/register`, {
      email,
      display_name: displayName,
    });
    const newCard = res.data.card;
    setCard(newCard);
    setUnlockedDocs(newCard.unlocked_documents || []);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ email: newCard.email, id: newCard.id }));
    return res.data;
  }, []);

  const refreshCard = useCallback(async () => {
    if (!card?.id) return;
    try {
      const res = await axios.get(`${API}/library-card/${card.id}/wallet`);
      setCard(prev => ({ ...prev, ...res.data }));
      const docsRes = await axios.get(`${API}/library-card/${card.id}/unlocked-docs`);
      setUnlockedDocs(docsRes.data.unlocked_documents || []);
    } catch (e) {
      console.error('Failed to refresh card:', e);
    }
  }, [card?.id]);

  const checkAccess = useCallback(async (documentId) => {
    try {
      const res = await axios.post(`${API}/library-card/check-access`, null, {
        params: { card_id: card?.id || '', document_id: documentId },
      });
      return res.data;
    } catch {
      return { has_access: false, reason: 'error' };
    }
  }, [card?.id]);

  const spendToken = useCallback(async (documentId) => {
    if (!card?.id) throw new Error('No library card');
    const res = await axios.post(`${API}/library-card/spend-token`, {
      card_id: card.id,
      document_id: documentId,
    });
    if (res.data.success) {
      setUnlockedDocs(prev => [...prev, documentId]);
      if (!res.data.is_infinite) {
        setCard(prev => ({ ...prev, token_balance: res.data.token_balance }));
      }
    }
    return res.data;
  }, [card?.id]);

  const redeemGumroad = useCallback(async (licenseKey, productPermalink) => {
    if (!card?.id) throw new Error('No library card');
    const res = await axios.post(`${API}/library-card/redeem-gumroad`, {
      card_id: card.id,
      license_key: licenseKey,
      product_permalink: productPermalink || 'mimet',
    });
    if (res.data.success) {
      setCard(prev => ({ ...prev, token_balance: res.data.new_balance }));
    }
    return res.data;
  }, [card?.id]);

  const logout = useCallback(() => {
    setCard(null);
    setUnlockedDocs([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const isDocUnlocked = useCallback((docId) => {
    if (isAdmin) return true;
    return unlockedDocs.includes(docId);
  }, [isAdmin, unlockedDocs]);

  return (
    <LibraryCardContext.Provider value={{
      card, hasCard, isAdmin, tokenBalance, loading,
      unlockedDocs, isDocUnlocked,
      register, refreshCard, checkAccess, spendToken, redeemGumroad, logout,
    }}>
      {children}
    </LibraryCardContext.Provider>
  );
}
